import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Emoji, EmojiEvent } from "@ctrl/ngx-emoji-mart/ngx-emoji";
import { CeloFileUploader } from "@modules/shared/file-upload";
import { ReplaySubject, Subscription } from "rxjs";
import { filter, first, map } from "rxjs/operators";
import { ConversationType, FeatureState, FullUserProfileModel } from "types";
import {
  FirebaseAnalyticsService,
  AudioRecordingService,
  ConService,
  MemoryService,
  MessageService,
  AccountService,
  isSupportedVideo,
} from "../../modules/core";
import { AudioPlaybackBarComponent } from "../audio-playback-bar/audio-playback-bar.component";
import { MessageComposeV2Component } from "../message-compose-v2/message-compose-v2.component";
import { ValidationError } from "../message-input/ValidationError";
import {
  MessageComposeModel,
  MessageInputErrorCode,
} from "./../message-input/message-input.component";

@Component({
  selector: "app-messages-bottom-text-bar",
  templateUrl: "./messages-bottom-text-bar.component.html",
  styleUrls: ["./messages-bottom-text-bar.component.scss"],
  providers: [AudioRecordingService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MessagesBottomTextBarComponent
  implements OnChanges, OnInit, OnDestroy, AfterViewInit
{
  @Output() uploadPhotos = new EventEmitter<any>();
  @Output() addPhotos = new EventEmitter<any>();
  @Output() attachFiles = new EventEmitter<any>();
  @Output() cancelQuote = new EventEmitter<any>();
  @Output() submitMessage = new EventEmitter<MessageComposeModel>();
  @Output() submitMessageMultiple = new EventEmitter();
  @Output() changeMessage = new EventEmitter<string>();
  @Output() validationEvent = new EventEmitter<ValidationError | null>();
  @Output() sendAudio = new EventEmitter<{ blob: Blob }>();

  @Input() conversationId;
  @Input() quotedMessage;
  @Input() conversation;
  @Input() conversationType;
  @Input() userAccount: FullUserProfileModel;
  @Input() pMap: { [key: string]: any };
  @Input() uploader: CeloFileUploader;
  @Input() participants: any[];
  @Input() public maxLength: number = 3000;

  @ViewChild("messageCompose")
  public messageCompose: MessageComposeV2Component | null = null;

  @ViewChild(AudioPlaybackBarComponent)
  public audioPlaybackBar: AudioPlaybackBarComponent | null = null;

  private setMessageComposeValueSubject =
    new ReplaySubject<MessageComposeModel>(1);

  emojiOpen: boolean;
  public initialValue: MessageComposeModel | null = null;

  public isValid: boolean = true;
  public submitTooltip: string = "";
  public isRecording: boolean = false;
  public hasRecording: boolean = false;
  public isPlaying: boolean = false;

  private subscriptions = new Subscription();
  private recordedBlob: Blob | null = null;

  public isVoiceMessagesVisible$ = this.accountService.userLimits$.pipe(
    map(
      (l) =>
        l.voice_messages &&
        l.voice_messages?.featureState !== FeatureState.Invisible &&
        this.conversationType !== ConversationType.External
    )
  );

  @HostListener("document:click", ["$event"])
  clickout(event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.emojiOpen = false;
    }
  }

  constructor(
    private connectionService: ConService,
    private memoryService: MemoryService,
    private messageService: MessageService,
    private analyticsService: FirebaseAnalyticsService,
    private eRef: ElementRef,
    private audioRecordingService: AudioRecordingService,
    private accountService: AccountService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    const changedConversationId = changes["conversationId"];
    if (changedConversationId) {
      this.audioRecordingService.deleteRecording();
      if (changedConversationId.previousValue) {
        this.connectionService.updateUserAction(
          changedConversationId.previousValue,
          false,
          "Typing"
        );
      }

      const messageDraft =
        this.memoryService.memory.messageDrafts[
          changedConversationId.currentValue
        ];
      this.setMessageComposeValueSubject.next(messageDraft);
    }
  }

  ngAfterViewInit(): void {
    this.setMessageComposeValueSubject.subscribe({
      next: (messageDraft) => {
        this.messageCompose?.setValue(messageDraft);
      },
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.messageService.messageQuoted.subscribe(() => {
        this.messageCompose?.restoreFocus();
      })
    );

    this.subscriptions.add(
      this.audioRecordingService.isRecording$.subscribe(
        (isRecording) => (this.isRecording = isRecording)
      )
    );

    this.subscriptions.add(
      this.audioRecordingService.isPlaying$.subscribe(
        (isPlaying) => (this.isPlaying = isPlaying)
      )
    );

    this.subscriptions.add(
      this.audioRecordingService.recordedBlob$.subscribe((blob) => {
        this.recordedBlob = blob;
        this.hasRecording = blob !== null;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.audioRecordingService.deleteRecording();
  }

  handleSubmit(message: MessageComposeModel) {
    this.submitMessage.emit(message);
    this.connectionService.updateUserAction(
      this.conversationId,
      false,
      "Typing"
    );
    this.emojiOpen = false;
    delete this.memoryService.memory.messageDrafts[this.conversationId];
  }

  emojiButton() {
    this.raiseMessageEvent("message_emoji");
  }

  attachButton() {
    this.attachFiles.emit();
    this.raiseMessageEvent("message_attach");
  }

  secureLibraryButton() {
    this.raiseMessageEvent("message_attach_secure_library");
  }

  uploadButton() {
    this.raiseMessageEvent("message_attach_local_gallery");
  }

  raiseMessageEvent(button_id) {
    this.analyticsService.buttonClickEvent(button_id, {
      flow: "message",
      conversation_type:
        this.analyticsService.ConversationTypes[this.conversationType],
    });
  }

  addEmoji({ emoji, $event }: EmojiEvent) {
    $event.preventDefault();
    this.messageService.onInsertEmojiSubject.next(event);
    this.messageCompose?.insertText(emoji.native);
  }

  public handleUserInput() {
    this.connectionService.updateUserAction(
      this.conversationId,
      true,
      "Typing"
    );
  }

  handleChange(message: MessageComposeModel) {
    this.updateMemory(message);
    this.emojiOpen = false;
  }

  updateMemory(message: MessageComposeModel) {
    this.memoryService.memory.messageDrafts[this.conversationId] = message;
  }

  handlePaste(e: ClipboardEvent) {
    const dataTransfer = (e.clipboardData ||
      e["originalEvent"]?.clipboardData) as DataTransfer;
    const items = dataTransfer.items;

    // Don't trigger upload if there is a text representation of the content on the clipboard as we
    // prefer to use this over other formats if available
    const hasText = dataTransfer.getData("text/plain");
    if (hasText) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (
        item.type.indexOf("image") !== 0 &&
        item.type.indexOf("application/pdf") !== 0 &&
        !isSupportedVideo(item)
      ) {
        continue;
      }

      const blob = item.getAsFile();
      e.preventDefault();
      if (blob) {
        this.uploader.addFiles([blob]);
      }
    }
  }

  public handleValidationError(validationError: ValidationError | null) {
    this.isValid = validationError === null;

    this.submitTooltip = "";
    if (validationError?.code === MessageInputErrorCode.MAX_LENGTH_EXCEEDED) {
      this.submitTooltip = `Message must be less then ${this.maxLength} characters`;
    }

    this.validationEvent.next(validationError);
  }

  recordAudio() {
    this.audioPlaybackBar?.startRecording();
  }

  sendAudioRecording() {
    if (this.recordedBlob) {
      this.sendAudio.emit({ blob: this.recordedBlob });
      this.audioPlaybackBar?.deleteRecording();
    } else {
      this.audioRecordingService.recordedBlob$
        .pipe(
          filter((blob) => blob !== null),
          first()
        )
        .subscribe((blob) => {
          this.sendAudio.emit({ blob });
          this.audioPlaybackBar?.deleteRecording();
        });
      this.audioRecordingService.stopRecording();
    }
  }

  onAudioPlaybackDelete() {
    this.audioPlaybackBar?.deleteRecording();
  }

  onAudioPlaybackBarClick($event: MouseEvent) {
    if (!this.audioPlaybackBar.isRecording) return;
    this.audioPlaybackBar?.onStop($event);
  }
}
